import {
  fetchBookmarks,
  addBookmark,
  removeBookmark,
  fetchDashboardWeeklySummary,
  fetchRecentlyPriceChangedProperty,
  fetchBookmarkPropertyOverview,
  updateBookmark,
} from "api/bookmarks";
import React, { createContext, useContext, useEffect, useState } from "react";
import { toast } from "react-toastify";
import { useOrganiseList } from "./OrganiseListContext";

export const BookmarkContext = createContext();

export const BookmarkProvider = ({ children }) => {
  const [loading, setLoading] = useState(false);
  const [isBookmarkFetching, setIsBookmarkFetching] = useState(false);
  const [bookmarkedProperties, setBookmarkedProperties] = useState([]);
  const [isWeeklySummaryFetching, setIsWeeklySummaryFetching] = useState(false);
  const [dashboardWeeklySummary, setDashboardWeeklySummary] = useState({});
  const [bookmarkedPropertyIds, setBookmarkedPropertyIds] = useState([]);
  const [recentPriceChangedProperties, setRecentPriceChangedProperties] =
    useState([]);
  const [
    isFetchingRecentPriceChangedProperty,
    setIsFetchingRecentPriceChangedProperty,
  ] = useState(false);
  const [
    isFetchingRecentBookmarkOverviewProperty,
    setIsFetchingRecentBookmarkOverviewProperty,
  ] = useState(false);
  const [recentBookmarkPropertyOverview, setRecentBookmarkPropertyOverview] =
    useState({});
  const { selectedOrganiseList } = useOrganiseList();
  const organizerListId = selectedOrganiseList?._id;
  const [isPropertyNoteDialogOpen, setIsPropertyNoteDialog] = useState(null);

  const togglePropertyNoteDialog = (id) => {
    setIsPropertyNoteDialog((prevId) => (prevId === id ? null : id));
  };

  const loadBookmarks = async (isPopulateProperty = false, nextUrl = null) => {
    setIsBookmarkFetching(true);
    try {
      const fetchedBookmars = await fetchBookmarks(
        isPopulateProperty,
        organizerListId,
        nextUrl
      );

      const ids =
        fetchedBookmars?.results?.map((bookmark) => {
          return {
            propertyId:
              typeof bookmark.propertyId === "string"
                ? bookmark.propertyId
                : bookmark.propertyId?._id,
            note: bookmark.note || "",
          };
        }) || [];

      if (isPopulateProperty) {
        if (nextUrl) {
          setBookmarkedProperties((prevVal) => ({
            ...fetchedBookmars,
            results: [...prevVal.results, ...fetchedBookmars.results],
          }));
        } else {
          setBookmarkedProperties(fetchedBookmars);
        }

        setBookmarkedPropertyIds((prevIds) => [
          ...prevIds,
          ...ids.filter((id) => !prevIds.includes(id)),
        ]);
      } else {
        setBookmarkedPropertyIds(ids);
      }
    } catch (err) {
      console.log(err.message || "An error occurred");
    } finally {
      setIsBookmarkFetching(false);
    }
  };

  const bookmarkProperty = async (propertyId, averageGrossYield) => {
    setLoading(true);
    try {
      await addBookmark(propertyId, organizerListId, averageGrossYield);
      toast.success(
        `Proprietà aggiunta correttamente nell’organizer alla lista ${selectedOrganiseList?.name}`
      );
    } catch (err) {
      toast.error(err.message || "An error occurred");
      throw new Error(err.message);
    } finally {
      setLoading(false);
    }
  };

  const removeSavedBookmark = async (propertyId) => {
    setLoading(true);
    try {
      await removeBookmark(propertyId, organizerListId);
      await loadBookmarks(true);
    } catch (err) {
      toast.error(err.message || "An error occurred");
      throw new Error(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Update the bookmark note
  const updateBookmarkNote = async (propertyId, note) => {
    setLoading(true);
    try {
      await updateBookmark(propertyId, organizerListId, note);
      toast.success("Bookmark note updated successfully.");
      setIsPropertyNoteDialog(null);
    } catch (err) {
      toast.error(err.message || "An error occurred");
      throw new Error(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Fetch dashboard weekly summary
  const getDashboardWeeklySummary = async () => {
    setIsWeeklySummaryFetching(true);
    try {
      const result = await fetchDashboardWeeklySummary();

      setDashboardWeeklySummary(result);
    } catch (err) {
      console.log(err);
    } finally {
      setIsWeeklySummaryFetching(false);
    }
  };

  // Fetch recent price changed property among the bookmark
  const getRecentPriceChangedProperty = async () => {
    setIsFetchingRecentPriceChangedProperty(true);
    try {
      const result = await fetchRecentlyPriceChangedProperty();
      setRecentPriceChangedProperties(result);
    } catch (err) {
      console.log(err.message);
    } finally {
      setIsFetchingRecentPriceChangedProperty(false);
    }
  };

  // Fetch last 7 days bookmark property overview
  const getRecentBookmarkPropertyOverview = async () => {
    setIsFetchingRecentBookmarkOverviewProperty(true);
    try {
      const result = await fetchBookmarkPropertyOverview(organizerListId);
      setRecentBookmarkPropertyOverview(result);
    } catch (err) {
      console.log(err.message);
    } finally {
      setIsFetchingRecentBookmarkOverviewProperty(false);
    }
  };

  return (
    <BookmarkContext.Provider
      value={{
        loadBookmarks,
        bookmarkedPropertyIds,
        bookmarkedProperties,
        bookmarkProperty,
        removeSavedBookmark,
        updateBookmarkNote,
        isBookmarkFetching,
        loading,
        getDashboardWeeklySummary,
        isWeeklySummaryFetching,
        dashboardWeeklySummary,
        getRecentPriceChangedProperty,
        isFetchingRecentPriceChangedProperty,
        recentPriceChangedProperties,
        getRecentBookmarkPropertyOverview,
        isFetchingRecentBookmarkOverviewProperty,
        recentBookmarkPropertyOverview,
        isPropertyNoteDialogOpen,
        togglePropertyNoteDialog,
        setIsPropertyNoteDialog,
      }}
    >
      {children}
    </BookmarkContext.Provider>
  );
};
